import { useState } from 'react';
import { ChevronDown, Goal, Repeat2 } from 'lucide-react';
import type { WebTask } from '../../../lib/tasks';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { timeAgo } from '../../../lib/notification-time';
import { newestRun } from '../../../lib/routine-run-group';
import { groupShowsWorkingNow, taskGroupStatusBadge } from '../../../lib/task-status';
import { workingNowCount } from '../../../lib/agent-task-buckets';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { AgentDot } from '../../AgentDot';
import type { ResolveKind, ResolvePhase } from '../../../lib/use-task-resolved-celebration';
import { TaskCard } from './TaskCard';
import { Badge, Card, Collapsible } from '../ui';
import { taskGoalOrigin } from '../../../lib/task-origin';
import { TaskOriginLine } from './TaskOriginLine';
import { useCategoryRowClass } from '../category-view/CategoryView';
import styles from './TaskOriginGroup.module.css';

// Consecutive tasks of ONE ORIGIN, folded into a single row (`groupTaskOrigins` in
// `lib/agent-task-buckets.ts` decides which fold). Two origins fold, at one shared threshold:
//   - a ROUTINE's runs — an hourly routine's fifteenth task of the day is a true record of work that
//     happened; the defect is only that fifteen of them bury the day's one-off work.
//   - a GOAL's laid-out plan — two tasks a goal put on the screen with nothing saying where they came
//     from is the reported bug this whole change exists for.
// Nothing is hidden either way: the header speaks for the newest task and every task is one expand away,
// rendered as its ordinary `TaskCard`.
//
// A group is one origin AND one outcome, which is what lets the header state a verdict ("7 Done")
// instead of a bare count. A task that ended differently breaks the group and gets its own row, so a fold
// can never speak for a pile it misrepresents — `needsYouCount` therefore equals the count on a
// waiting group and zero on every other, and stays only because the native ports mirror the field.
export function TaskOriginGroup({
  tasks,
  needsYouCount,
  spaceName,
  spaceSlug,
  agentName,
  goalId,
  resolveOf,
}: {
  tasks: WebTask[];
  needsYouCount: number;
  spaceName: (id?: string) => string | undefined;
  /** The task's Space slug — what makes a goal's route resolvable (see `lib/task-origin.ts`). */
  spaceSlug: (id?: string) => string | undefined;
  agentName: (id?: string) => string | undefined;
  /** Set when the fold is a GOAL's plan rather than a routine's runs — the header then names the goal
   *  and wears the goals mark. */
  goalId?: string;
  /** The list's farewell lookup (`useResolvedTaskFarewell`, owned by `TasksBody`) — a fold is just
   *  another place a `TaskCard` renders, so a task inside it must animate its own row the same way one
   *  outside a fold does. */
  resolveOf?: (id?: string) => { phase: ResolvePhase; kind: ResolveKind } | undefined;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const rowSurface = useCategoryRowClass();

  const newest = newestRun(tasks) ?? tasks[0];
  const title = (newest?.title ?? '').trim() || t('tasks.untitledTask');
  const agent = agentName(newest?.agentId);
  // The fold's status from EVERY task in it (`taskGroupStatus`: the most urgent state wins, decision-aware
  // like each card below) — "3 waiting on you", "2 active tasks" — in a countable key pair so the count and
  // the noun agree. Reading one task's badge is how a fold came to say "3 Active Task" over cards that each
  // read "Waiting on you".
  const statusBadge = taskGroupStatusBadge(tasks);
  // Live runs INSIDE the fold — `runInFlight === true` only, so a fold whose run state was never computed
  // says nothing rather than guessing. One live run is enough for the fold to say so; the count is how
  // many of its tasks have a loop executing right now.
  const workingNow = workingNowCount(tasks);
  // Same meta line as `TaskCard`: agent dot + name, then "{Space} · {when}" (Figma 13641-101528).
  const where = [spaceName(newest?.workspaceId), timeAgo(newest?.updatedDate ?? null)].filter(Boolean).join(' · ');

  // The origin this fold speaks for, named above the header exactly as it is named above a single task's
  // row — same line, same position, same weight — so a plan of two reads like a plan of one.
  const goalOrigin = goalId ? taskGoalOrigin({ goalId, goalTitle: newest?.goalTitle }, spaceSlug(newest?.workspaceId)) : null;

  const toggle = () => {
    const next = !open;
    const buttonName = next ? 'expand_routine_group' : 'collapse_routine_group';
    logPageClickBi({ ...TASKS_PAGE, button_name: buttonName, task_id: newest?.id }, { componentName: buttonName.replace(/_/g, '-') });
    setOpen(next);
  };

  // In a category list a goal's plan is not folded (Figma 21055:247683): the "From goal" header row, then
  // every task as a full row of its own — its badge, its ⋮ and its description. A routine's runs stay
  // folded, because one routine can carry dozens of them.
  if (rowSurface && goalId) {
    return (
      <div className={styles.goalRows}>
        {goalOrigin && <TaskOriginLine origin={goalOrigin} />}
        {tasks.map((task) => (
          <div key={task.id} className={styles.goalRow}>
            <TaskCard
              task={task}
              hideOrigin
              spaceName={spaceName(task.workspaceId)}
              spaceSlug={spaceSlug(task.workspaceId)}
              agentName={agentName(task.agentId)}
              resolvePhase={resolveOf?.(task.id)?.phase}
              resolveKind={resolveOf?.(task.id)?.kind}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={rowSurface ? `${styles.group} ${styles.groupInList}` : styles.group}>
      {goalOrigin && <TaskOriginLine origin={goalOrigin} />}
      <Card
        component="button"
        interactive
        aria-expanded={open}
        className={[styles.header, open ? styles.headerOpen : '', rowSurface].filter(Boolean).join(' ')}
        onClick={toggle}
      >
        <div className={styles.headerTop}>
          <span className={styles.heading}>
            <span className={styles.title} dir="auto">
              {title}
            </span>
            <span className={styles.badges}>
              {/* The origin's glyph, because the count alone never said WHY these rows are one row. The
                  card looked like an unlabelled pile of seven things; this is what marks it as a
                  routine's runs, or a goal's plan. */}
              <span className={styles.originMark} aria-hidden>
                {/* The routines mark is `repeat-2` app-wide (see `RoutinesSummary`); the goals mark is
                    `target`, the same one `TaskOriginLine` wears. */}
                {goalId ? <Goal size={14} /> : <Repeat2 size={14} />}
              </span>
              {/* The group's shared outcome, in the same Badge style a single task of that status wears —
                  a fold of finished work now reads "7 Done" rather than a verdict-free "7 runs". The
                  grouping guarantees one outcome per group, so this can never speak for a mixed pile. */}
              <Badge
                variant={statusBadge.variant}
                label={t(statusBadge.labelKey, { count: tasks.length })}
              />
              {/* The fold's live count rides BESIDE the outcome chip, never instead of it: "{n} working
                  now" says which of the folded runs have a loop executing at this moment — except beside "Working",
                  which already says it (`groupShowsWorkingNow`). */}
              {groupShowsWorkingNow(statusBadge.status, workingNow) && (
                <Badge variant="in-progress" label={t('tasks.chip.workingNowCount', { count: workingNow })} />
              )}
            </span>
          </span>
          <ChevronDown size={20} aria-hidden className={[styles.chevron, open ? styles.chevronOpen : ''].filter(Boolean).join(' ')} />
        </div>
        {(agent || where) && (
          <div className={styles.meta}>
            {agent && (
              <span className={styles.agentTag}>
                {newest?.agentId ? <AgentDot id={newest.agentId} size={15} /> : null}
                <span className={styles.agentName}>{agent}</span>
              </span>
            )}
            {agent && where ? <span className={styles.metaSep}>|</span> : null}
            {where ? <span className={styles.metaText}>{where}</span> : null}
          </div>
        )}
      </Card>
      <Collapsible open={open} innerClassName={styles.runs}>
        <div className={styles.runsInner}>
          {tasks.map((task) => (
            // The fold's header already names the origin for all of them, so a run inside it does not
            // repeat it — passing no `spaceSlug` is what suppresses the per-row line.
            <TaskCard
              key={task.id}
              task={task}
              spaceName={spaceName(task.workspaceId)}
              agentName={agentName(task.agentId)}
              resolvePhase={resolveOf?.(task.id)?.phase}
              resolveKind={resolveOf?.(task.id)?.kind}
            />
          ))}
        </div>
      </Collapsible>
    </div>
  );
}
